"use client";

import React, { useState, useEffect } from "react";
import InputField from "@/app/components/input";
import User from "@/app/components/svg/user";
import PhoneDef from "@/app/components/svg/phone-def";
import EmailLight from "@/app/components/svg/email-light";
import Button from "../button";
import Checkbox from "../checkbox";
import EmailDark from "../svg/email-dark";
import ThankYouModal from "../thank-you";
import Loader from "../svg/loader";

interface ContactFormProps {
    title: string;
    btntext: string;
    lang: string;
}

const ContactForm: React.FC<ContactFormProps> = ({ title, btntext, lang }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);


    const [formData, setFormData] = useState({
        fullName: "",
        phoneNumber: "",
        email: "",
        comments: "",
    });

    const [isChecked, setIsChecked] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        if (isSubmitted) {
            setTimeout(() => setIsVisible(true), 50); 
            
            setTimeout(() => {
                setIsVisible(false);
                setIsSubmitted(false);
            }, 3000);
        }
    }, [isSubmitted]);

    const handleChange = (name: string, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (checked: boolean) => {
        setIsChecked(checked);
    };

    const requiredText = lang === 'en' ? 'This field is required' : 'Detta fält är obligatoriskt'

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!formData.fullName) newErrors.fullName = requiredText
        if (!formData.phoneNumber) newErrors.phoneNumber = requiredText;
        if (!formData.email) newErrors.email = requiredText;
        if (!isChecked) newErrors.checkbox = lang === 'en' ? 'Agreement to terms and conditions is required' : 'Godkännande av villkor krävs'

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setSubmitError(null);

        try {
            const response = await fetch('/api/send-email', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    formType: 'contact', // Явно указываем тип формы
                    formData: {         // Обертываем данные в formData
                        fullName: formData.fullName,
                        phone: formData.phoneNumber, // Обратите внимание на phone вместо phoneNumber
                        email: formData.email,
                        comments: formData.comments
                    }
                }),
            });

            const result = await response.json();

            if (!response.ok || !result.success) {
                throw new Error(result.error || 'Failed to send form');
            }

            setIsSubmitted(true);
            setFormData({
                fullName: "",
                phoneNumber: "",
                email: "",
                comments: "",
            });
            setIsChecked(false);
        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitError(error instanceof Error ? error.message : 'An unknown error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="relative">
            {isSubmitted && (
                <div className={`fixed inset-0 flex items-center justify-center bg-[#00000080] z-10 transition-opacity duration-500 ease-in-out 
                    ${isVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}>
                    <div className={`transform transition-transform duration-500 ease-in-out 
                        ${isVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`}>
                        <ThankYouModal title="Thank you!" text="Your message has been sent successfully." />
                    </div>
                </div>
            )}

            <div className="rounded-[40px] p-[48px] shadow-[0_10px_24px_0_#00000026] max-[576px]:p-[20px] max-[576px]:rounded-[20px]">
                <h2 className="text-[32px] font-bold text-violet-500 mb-[40px] max-[576px]:text-[20px] max-[576px]:mb-[24px]">{title}</h2>
                <form onSubmit={handleSubmit} className="space-y-[32px] max-[576px]:space-y-[24px]">
                    <InputField
                        label={lang === 'en' ? 'Full Name' : 'fullständigt namn'}
                        iconLeft={<User />}
                        placeholder={lang === 'en' ? 'Write your full name' : 'Skriv ditt fullständigt namn'}
                        type="text"
                        onChange={(e) => handleChange("fullName", e.target.value)}
                        errorMessage={errors.fullName}
                    />
                    <InputField
                        label={lang === 'en' ? 'Phone Number' : 'telefonnummer'}
                        iconLeft={<PhoneDef />}
                        placeholder={lang === 'en' ? 'Write your phone number' : 'Skriv ditt telefonnummer'}
                        type="number"
                        onChange={(e) => handleChange("phoneNumber", e.target.value)}
                        errorMessage={errors.phoneNumber}
                    />
                    <InputField
                        label={lang === 'en' ? 'E-mail' : 'e-post'}
                        iconLeft={<EmailLight />}
                        placeholder={lang === 'en' ? 'Write your e-mail' : 'Skriv din e-post'}
                        type="email"
                        onChange={(e) => handleChange("email", e.target.value)}
                        errorMessage={errors.email}
                    />
                    <InputField
                        label={lang === 'en' ? 'Write Your Message' : 'SKRIV DITT MEDDELANDE'}
                        type="textarea"
                        placeholder={lang === 'en' ? 'Write text' : 'Skriv text'}
                        onChange={(e) => handleChange("comments", e.target.value)}
                    />
                    <Checkbox
                        label={lang === 'en' ? 'I consent to my personal data being processed in accordance with the ' : 'Jag samtycker till att mina personuppgifter behandlas i enlighet med'}
                        required={true}
                        checked={isChecked}
                        onChange={handleCheckboxChange}
                        link={`${lang === 'en' ? '/en' : ''}/integritetspolicy`}
                        linktext={lang === 'en' ? 'privacy policy' : ' integritetspolicyn'}
                    />
                    {errors.checkbox && <span className="text-red-500 text-sm">{errors.checkbox}</span>}

                    <div className="flex justify-end">
                    <Button
                        size="big"
                        type="primary"
                        mobileFull="yes"
                        text={isLoading ? lang === 'en' ? 'Sending...' : 'Sändning...' : btntext}
                        icon={isLoading ? <Loader/> : <EmailDark />}
                        iconPostiton="right"
                    />

                    </div>
                </form>
            </div>
        </div>
    );
};

export default ContactForm;
